import React, { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { ArrowLeft, Trash2 } from 'lucide-react';

import { Button } from '@epoch-ui/components/Button';
import { ModalTitle } from '@epoch-ui/components/Modal/modal';
import { SessionCard, SessionCardTitleAndDate } from 'components/cards/SessionCard';
import { RsvpSticker } from 'components/cards/components/RsvpSticker/RsvpSticker';
import { PlusOneGuestForm } from 'components/discover/event/EventView/PlusOneForm/PlusOneGuestForm';
import { PlusOneFormValue } from 'components/discover/event/EventView/PlusOneForm';
import { EventType } from 'types/event.types';
import dayjs from 'utils/extendedDayJS';

import { useGuestSideSheetContext } from '../../guestSideSheetContext';

import { usePlusOneGuestModalContext } from './plusOneGuestModalContext';

export const PlusOneGuestFormContent: FC = () => {
  const form = useFormContext<PlusOneFormValue>();

  const { dispatch, handleRemoveGuest, savingPlusOneGuest, removingPlusOneGuest } =
    usePlusOneGuestModalContext();

  const {
    selectedPlusOneGuest: plusOneGuest,
    guestData: { guest, event },
    selectedSession
  } = useGuestSideSheetContext();

  const isEditMode = !!plusOneGuest?.plusOneId;

  const isMultiSessionEvent = event.eventType === EventType.MULTI_SESSIONS;
  const plusOneConfig = (isMultiSessionEvent
    ? selectedSession?.plusOneConfig
    : event.plusOneConfig) ?? { collectQuestionnaireResponses: false };
  const preEventQuestions = event.preEventQuestions || [];
  const eventTimezone = event.timezoneCode || 'UTC';
  const displayedTimezone = dayjs.tz.guess() || eventTimezone;

  const { collectQuestionnaireResponses } = plusOneConfig;
  const questionsToShow = collectQuestionnaireResponses ? preEventQuestions : [];

  return (
    <form
      onSubmit={form.handleSubmit((values: PlusOneFormValue) =>
        dispatch({ type: 'SUBMIT_FORM', values })
      )}
      className="tw-flex tw-min-h-0 tw-flex-col tw-space-y-5"
    >
      <ModalTitle className="tw-mb-0 tw-shrink-0 tw-text-2xl tw-font-medium">
        {isEditMode ? 'Edit guest information' : 'Add new guest'}
        {isMultiSessionEvent && !event.limitRegistration && !isEditMode && (
          <Button
            type="button"
            variant="ghost"
            size="xxs"
            className="tw-mt-2 tw-flex tw-h-5 tw-items-center tw-gap-1 tw-p-0 tw-text-sm tw-font-normal tw-leading-5 tw-text-primary-grey hover:tw-bg-transparent"
            onClick={() => dispatch({ type: 'RETURN_TO_SELECT_GUEST' })}
          >
            <ArrowLeft size={16} />
            Back to guests
          </Button>
        )}
      </ModalTitle>

      {isMultiSessionEvent && selectedSession && !isEditMode && (
        <SessionCard
          session={selectedSession}
          eventTimezone={eventTimezone}
          displayedTimezone={displayedTimezone}
          className="tw-rounded-xl"
        >
          <SessionCardTitleAndDate />
          <RsvpSticker className="tw-rounded-xl tw-p-2" rsvpStatus={guest.status} />
        </SessionCard>
      )}

      <div className="tw-mt-5 tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-y-auto tw-rounded-3xl tw-border tw-border-solid tw-border-border-grey tw-px-8 tw-py-6">
        <PlusOneGuestForm
          collectQuestionnaireResponses={
            collectQuestionnaireResponses && questionsToShow.length > 0
          }
          questions={questionsToShow}
          disabled={savingPlusOneGuest}
        >
          <div className="tw-flex tw-flex-row tw-justify-between">
            <Button
              type="submit"
              size="xxs"
              variant="primary"
              className="tw-rounded-xl"
              disabled={removingPlusOneGuest || !form.formState.isDirty}
            >
              {isEditMode ? 'Save changes' : 'Add guest'}
            </Button>
            <Button
              type="button"
              size="xxs"
              variant="destructive"
              className="tw-rounded-xl"
              onClick={handleRemoveGuest}
              loading={removingPlusOneGuest}
              disabled={removingPlusOneGuest}
            >
              <Trash2 size={16} className="tw-mr-1" />
              Remove guest
            </Button>
          </div>
        </PlusOneGuestForm>
      </div>
    </form>
  );
};
